import { IData } from "./dataInterfaces";

// Supported languages
export enum Language {
  EN = "en",
  SV = "sv"
}

export enum DataType {
  User     = "users",
  Company  = "company",
  Course   = "course",
  Program  = "program",
  Package  = "collection",
  Step     = "step",
  Quiz     = "quiz",
  Task     = "task",
  Choice   = "choice",
  File     = "document",
  Video    = "video",
}

export type DataTypeContent = DataType.Course | DataType.Package | DataType.Program;

export enum ProgramType {
  Default = "default"
}

export enum PackageType {
  Default = "default"
}

export enum StepType {
  Read    = "read",
  Quiz    = "quiz",
  Video   = "video",
  Grid    = "grid",
}

export enum DataStatus {
  Draft     = "draft",
  Published = "published",
  Archived  = "archived"
}

export enum LearningModality {
  ELearning = "e-learning",
} 

export enum TaskType {
  Single = "single",
  Multi  = "multiple",
  Input  = "input",
  Column = "column",
}

export enum UserType {
  Participant = "participant",
  Manager     = "manager",
  Admin       = "admin",
  Analyst     = "analyst",
  Editor      = "editor",
}

export const SystemUsers = [
  UserType.Admin, 
  UserType.Analyst, 
  UserType.Editor
];

export enum Services {
  Account          = "account",
  Feedback         = "feedback",
  BankID           = "bankid",
  Catalog          = "catalog",
  Choice           = "choice",
  Company          = "company",
  CompanyUser      = "company_user",
  CompanyWhitelist = "company_whitelist",
  Course           = "course",
  CourseLicense    = "course_license", // backend-only
  CourseUser       = "course_user",
  Document         = "document",
  Email            = "email",
  Evaluation       = "evaluation",
  GroupUser        = "group_user",
  Invite           = "invite",
  License          = "license",
  Package          = "collection",
  PackageUser      = "package_user",
  Program          = "program",
  ProgramCourse    = "program_course",
  ProgramLicense   = "program_license", // backend-only
  ProgramUser      = "program_user",
  Progress         = "progress",
  Quiz             = "quiz",
  Statistics       = "statistics",
  Step             = "step",
  Storage          = "storage",
  Task             = "task",
  Trigger          = "trigger",
  Users            = "users",
  Video            = "video",
  Vimeo            = "vimeo",
  Session          = "session",
  Swedsec          = "swedsec",
}

export const contentServices: Services[] = [
  Services.Choice,
  Services.Package,
  Services.Course,
  Services.Document,
  Services.Program,
  Services.ProgramCourse,
  Services.Quiz,
  Services.Step,
  Services.Task,
  Services.Video,
];

export type VideoChapter = {
  id: string
  second_from: number
  second_to: number
  title: string
}


/**
 * Enum that can be used directly as keys for i18n to receive 
 * validation error messeages.
 * 
 * Note: Use the @validation namesapce, i.e.
 * t(TaskValidationError.Valid, {ns: "validation"});
 */

export enum TaskValidationError {
  Valid                        = "valid",
  EmptyAnswer                  = "empty_answer",
  EmptyQuestion                = "empty_question",
  EmptyChoice                  = "empty_choice",
  NoCorrectChoice              = "no_correct_choice",
  InsufficientChoices          = "insufficient_choices",
  InsufficientIncorrectChoices = "insufficient_incorrect_choices",
}

/**
 * Enum that can be used directly as keys for i18n to receive 
 * validation error messeages.
 * 
 * Note: Use the @validation namesapce, i.e.
 * t(QuizValidationError.Valid, {ns: "validation"});
 */
export enum QuizValidationError {
  Valid                        = "valid",
  TasksRequired                = "tasks_required",
  NoCorrectPassConstraint      = "no_correct_pass_constraint",
  TooManyCorrectPassConstraint = "too_many_correct_pass_constraint",
  InsufficientTasks            = "insufficient_tasks",
  InvalidTask                  = "invalid_task",
}

/**
 * Format of users added by importing excel. This how the name of the headers need to be set. 
 */
export type XLSX_UserRow = {
  personal_number: string,
  email:           string,
  first_name:      string,
  last_name:       string,
  title:           string,
  phone:           string,
  id?:             number,
}

/**
 * Format of users added by importing excel. This how the name of the headers need to be set. 
 */
export enum ErrorMessage {
  UserExists = 'error_user_already_exists'
}

export enum HierarchyTypes {
  Company = "company",
  Program = "program",
  Course  = "course",
  User    = "users",
  Quiz    = "quiz",
}

export type TreeData = Partial<Record<HierarchyTypes, {[tree_id: number]: TreeDataItem}>>;

export type TreeDataItem = IData & {
  id:             number,
  tree_id:        number,
  hierarchy_type: HierarchyTypes,
  label:          string,
  checked?:       boolean,
  progress?:      number, 
  progress_raw?:  number[],
  /* 
  Course_User:  Progress
  Quiz:         None
  Program_User: Average over courses of that program
  User:         Average over all course_users
  Company:      Average over all users of that company
  */
  attempts?:      number,
  attempts_raw?:  number[],
  /*
  Course_user:  # of attempts of final quiz
  Quiz:         # of attempts on progress for that step
  Program_User: Average over courses of that program
  User:         none
  Company:      none
  */
  duration?:     number, // in seconds
  duration_raw?: number[],
  /*
  Course_user:  completed - activated of a course
  Program_User: Average over courses of that program
  User:         average over all course_users
  Quiz:         none
  Company:      average over all users
  */
  last_activity?:     Date,
  last_activity_raw?: Date[],
  /**
   * Course_user:  time of last progress made on a course
   * program_user: time of last progress made on a course of that program
   * user: time of last progress made by that user
   * quiz:         none
   * company:      time of last progress made by a user of that company
   */
  state?:         "completed" | "started" | "not_started",
  state_raw?:     ("completed" | "started" | "not_started")[],
  /**
   * course_user:  started/completed or not
   * program_user: courses.any(started) || courses.all(completed)
   * user:         courses.any(started) || courses.all(completed)
   * quiz:         none
   * company:      users.courses.any(started) || users.courses.all(completed)
   */
};

export type TreeLinks = {[id: number]: number[]}[]; // the i-th array entry will map ids of TreeData depth i to i+1
export type SessionData = {
  sessions:       string[],
  total_sessions: number,
}